import React, {FC, useEffect, useMemo, useRef, useState} from "react";
import styled from "styled-components";
import Modal from "antd/es/modal/Modal";
import {Checkbox, Input, Tooltip, message as antdMessage} from "antd";
import {CloseOutlined, InfoCircleOutlined, WarningOutlined} from "@ant-design/icons";
import {CustomButton} from "../../../../../components/UI/AntCustom/CustomButton";
import {defineMessages, useIntl} from "react-intl";
import {CommonButtonMessages} from "../../../../../intl/commonMessages/CommonButtonMessages";
import FuelIcon from "../../../../../media/icons/fuel.svg?react";
import PassengerIcon from "../../../../../media/icons/passenger-icon.svg?react";
import AcceptIcon from "../../../../../media/icons/accept.svg?react";
import {DebounceSelect} from "../../../../../components/UI/AntCustom/DebounceSelect";
import {fetchCurrencies, getInitialCurrencyForSelect} from "../../../../../utils/helpers/selectDataFetchers/fetchCurrencies";
import {ICurrency} from "../../../../../store/reducers/fleetReducer/fleetReducerTypes";
import {fetchOwnPlanes} from "../../../../../utils/helpers/selectDataFetchers/fetchOwnPlanes";
import {CustomModal} from "../../../../../components/UI/AntCustom/CustomModal";
import {DateAndTimeRangePicker} from "../../../../../components/UI/AntCustom/DateAndTimeRangePicker";
import {DateAndTimePicker} from "../../../../../components/UI/AntCustom/DateAndTimePicker";
import dayjs, {Dayjs} from "dayjs";
import utc from "dayjs/plugin/utc";
import {useAppSelector} from "../../../../../store/store";
import {TechnicalStopsModal} from "./TechnicalStopsModal";
import {API} from "../../../../../API/API";
import {AirportApiGetAirportRequest, BookingApiOperatorSubmitOfferRequest, OrderRequestPlaneOfferEntity} from "../../../../../proto/generated/api_entities_pb";
import {Logger} from "../../../../../utils/logger/Logger";
import {OrderRequestPlaneStatusEnum} from "../../../../../utils/constans/statuses/orderRequestStatuses";
import {getErrorMessageByCode} from "../../../../../utils/constans/errorCodes/errorCodes";
import {amplitudeEvents} from "../../../../../utils/analytics/amplitude";
import {useIsMobile} from "../../../../../hooks/useWindowSize";
import {mediaScreenSizes} from "../../../../../utils/constans/styles/screenSizes";
import {IAirport} from "../../../../../store/reducers/fleetReducer/fleetReducerTypes";
import {LocalTimeDisplay} from "../../../../../components/businessCommon/localTimeDisplay/LocalTimeDisplay";
import {useCurrencyConverter} from "../../../../../hooks/useCurrencyConverter";
import {IFreighterOrderRouteOrderRequest} from "../../../../../store/reducers/freighterRequestsReducer/freighterRequestsReducerTypes";
import {formatDate} from "../../../../../utils/helpers/dateHelpers/dateHelpers";
import RouteArrowIcon from "../../../../../media/icons/route_arrow.svg?react";
import {DisplayFlyPrice} from "../../../../../utils/helpers/textDisplayHelpers/DisplayFlyPrice";
import {DisplayFlyDistance} from "../../../../../utils/helpers/textDisplayHelpers/DisplayFlyDistance";
import {DistanceUnit} from "../../../../../utils/constans/statuses/distanceUnits";

dayjs.extend(utc);

// Функция для получения символа валюты по коду
const getCurrencySymbol = (currencyCode: string): string => {
  const currencyMap: Record<string, string> = {
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'JPY': '¥',
    'CNY': '¥',
    'RUB': '₽',
    'CHF': 'CHF',
    'AUD': 'A$',
    'CAD': 'C$',
    'NZD': 'NZ$',
    'SEK': 'kr',
    'NOK': 'kr',
    'DKK': 'kr',
    'PLN': 'zł',
    'TRY': '₺',
    'INR': '₹',
    'BRL': 'R$',
    'MXN': '$',
    'ZAR': 'R',
    'KRW': '₩',
    'SGD': 'S$',
    'HKD': 'HK$',
    'THB': '฿',
  };
  
  // Если код валюты уже является символом (например, "$"), возвращаем его
  if (currencyCode.length <= 2 && /[^A-Za-z0-9]/.test(currencyCode)) {
    return currencyCode;
  }
  
  // Ищем в маппинге
  const upperCode = currencyCode.toUpperCase();
  return currencyMap[upperCode] || currencyCode;
};

const modalMessages = defineMessages({
  respondTitle: {
    id: "respondRequestModal.title",
    defaultMessage: "Отклик на запрос {request}"
  },
  sectionTitle: {
    id: "respondRequestModal.sectionTitle",
    defaultMessage: "Детали перелёта"
  },
  aircraftLabel: {
    id: "respondRequestModal.aircraft",
    defaultMessage: "Самолет"
  },
  routeLabel: {
    id: "respondRequestModal.routeUtc",
    defaultMessage: "Дата маршрута (UTC)"
  },
  requestedLabel: {
    id: "respondRequestModal.requested",
    defaultMessage: "Requested"
  },
  priceLabel: {
    id: "respondRequestModal.price",
    defaultMessage: "Цена"
  },
  currencyLabel: {
    id: "respondRequestModal.currency",
    defaultMessage: "Валюта"
  },
  durationLabel: {
    id: "respondRequestModal.duration",
    defaultMessage: "Время в пути"
  },
  passengersLabel: {
    id: "respondRequestModal.passengers",
    defaultMessage: "Пассажиры"
  },
  refuelingsLabel: {
    id: "respondRequestModal.refuelings",
    defaultMessage: "Дозаправки"
  },
  warningText: {
    id: "respondRequestModal.warning",
    defaultMessage: "Убедитесь, что выбранное время полёта совпадает с часами работы аэропорта"
  },
  positioningNote: {
    id: "respondRequestModal.positioningNote",
    defaultMessage: "Цена включает позиционирование из аэропорта {airportCode}, время {time}, расстояние {distance}"
  },
  confirmationText: {
    id: "respondRequestModal.confirmation",
    defaultMessage: "Подтверждаю, что ознакомлен с расписанием работы аэропорта и выбрал подходящее время"
  },
  priceRequiredError: {
    id: "respondRequestModal.priceRequiredError",
    defaultMessage: "Price is required"
  },
  invalidPriceError: {
    id: "respondRequestModal.error.invalidPrice",
    defaultMessage: "Error: price must be a positive number for aircraft {aircraftCode}"
  },
  noCurrencyError: {
    id: "respondRequestModal.error.noCurrency",
    defaultMessage: "Error: currency is not specified for aircraft {aircraftCode}"
  },
  letterLabel: {
    id: "respondRequestModal.letterLabel",
    defaultMessage: "Сопроводительное письмо"
  },
  letterPlaceholder: {
    id: "respondRequestModal.letterPlaceholder",
    defaultMessage: "Приветственное сообщение увидят брокеры в личных сообщениях"
  },
  addAircraft: {
    id: "respondRequestModal.addAircraft",
    defaultMessage: "+ Добавить самолет"
  },
  requestPlaceholder: {
    id: "respondRequestModal.requestPlaceholder",
    defaultMessage: "Charterer-2"
  },
  requestedTooltip: {
    id: "respondRequestModal.requestedTooltip",
    defaultMessage: "Запрошенная клиентом стоимость"
  },
  dateTimeModalTitle: {
    id: "respondRequestModal.dateTimeModalTitle",
    defaultMessage: "Выберите дату и время маршрута (UTC)"
  },
  respondTooltip: {
    id: "respondRequestModal.respondTooltip",
    defaultMessage: "Подтвердите все самолеты и подтвердите рабочее время для продолжения работы"
  },
  interactWithAllAircraftTooltip: {
    id: "respondRequestModal.interactWithAllAircraftTooltip",
    defaultMessage: "Взаимодействуйте со всеми самолетами"
  }
});

type OfferStatus = "pending" | "accepted" | "declined";

export interface RequestOffer {
  id: string;
  aircraftCode: string;
  planeId?: number; // ID самолета для отправки на бекенд
  orderRequestPlaneId?: number; // ID OrderRequestPlane для обновления существующего предложения
  departureDate: string;
  departureTime: string;
  arrivalDate: string;
  arrivalTime: string;
  requestedAmount: string;
  requestedCurrency: string;
  requestedCurrencyId?: number; // Currency ID for the requested price (plane's currency)
  brokerRequestedCurrencyId?: number; // Broker's original requested currency ID (for conversion)
  offerAmount: string;
  offerCurrency: string;
  offerCurrencyId?: number;
  flightDuration: string;
  passengers: number;
  refuels: number;
  status: OfferStatus;
  positioningDurationMinutes?: number; // Positioning flight duration in minutes
  positioningDistanceKm?: number; // Positioning flight distance in kilometers
  positioningFromAirportId?: number; // Airport ID where aircraft is located before positioning
  positioningDepartureDate?: string; // Exact datetime when positioning flight departs (YYYY-MM-DD HH:MM:SS)
}

interface RespondToRequestModalProps {
  visible: boolean;
  onClose: () => void;
  requestName?: string;
  orderRequestId?: number;
  offers?: RequestOffer[];
  onSubmit?: (payload: {offers: RequestOffer[]; message: string; confirmed: boolean}) => void;
  onAddAircraft?: () => void;
  readOnly?: boolean; // Режим просмотра - все поля disabled, кнопки скрыты
  onSuccess?: () => void; // Callback после успешной отправки, вызывается перед закрытием модалки
  airportFrom?: IAirport; // Аэропорт отправления для отображения локального времени
  airportTo?: IAirport; // Аэропорт прибытия для отображения локального времени
  brokerComment?: string; // Сообщение от брокера (order.comment)
  orderRouteOrderRequests?: IFreighterOrderRouteOrderRequest[]; // Маршруты заказа для отображения запрошенных дат
}

interface LocalOfferState extends RequestOffer {}

export const respondModalMockOffers: RequestOffer[] = [
  {
    id: "N511WW",
    aircraftCode: "N511WW",
    departureDate: "08.07.22",
    departureTime: "09:00",
    arrivalDate: "08.07.22",
    arrivalTime: "12:00",
    requestedAmount: "22,800",
    requestedCurrency: "$",
    offerAmount: "16277",
    offerCurrency: "USD",
    flightDuration: "4h 26m",
    passengers: 4,
    refuels: 1,
    status: "pending"
  },
  {
    id: "N900KG",
    aircraftCode: "N900KG",
    departureDate: "08.07.22",
    departureTime: "09:00",
    arrivalDate: "08.07.22",
    arrivalTime: "12:00",
    requestedAmount: "22,800",
    requestedCurrency: "$",
    offerAmount: "16277",
    offerCurrency: "USD",
    flightDuration: "4h 26m",
    passengers: 4,
    refuels: 1,
    status: "accepted"
  },
  {
    id: "N1776C",
    aircraftCode: "N1776C",
    departureDate: "08.07.22",
    departureTime: "09:00",
    arrivalDate: "08.07.22",
    arrivalTime: "12:00",
    requestedAmount: "22,800",
    requestedCurrency: "$",
    offerAmount: "16277",
    offerCurrency: "USD",
    flightDuration: "4h 26m",
    passengers: 4,
    refuels: 1,
    status: "declined"
  }
];

export const RespondToRequestModal: FC<RespondToRequestModalProps> = ({
  visible,
  onClose,
  requestName,
  orderRequestId,
  offers,
  onSubmit,
  onAddAircraft,
  readOnly = false,
  onSuccess,
  airportFrom,
  airportTo,
  brokerComment,
  orderRouteOrderRequests = []
}) => {
  const intl = useIntl();
  const isMobile = useIsMobile();
  const company = useAppSelector(state => state.company.oneItemObject.item);
  const userCurrency = useAppSelector(state => state.user.oneItemObject.item?.currency || state.company.oneItemObject.item?.currency);
  const userDistanceUnit = useAppSelector(state => state.user.oneItemObject.item?.distanceUnit) || DistanceUnit.NAUTICAL_MILE;
  const {convertSync, isReady: isCurrencyConverterReady} = useCurrencyConverter();
  const [localOffers, setLocalOffers] = useState<LocalOfferState[]>(offers || []);
  const [confirmationChecked, setConfirmationChecked] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [dateTimeModalVisible, setDateTimeModalVisible] = useState<boolean>(false);
  const [editingOfferId, setEditingOfferId] = useState<string | null>(null);
  const [dateTimeRange, setDateTimeRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [selectedAircraftValue, setSelectedAircraftValue] = useState<any>(undefined);
  const [aircraftSelectOpen, setAircraftSelectOpen] = useState<boolean>(false);
  const [technicalStopsModalVisible, setTechnicalStopsModalVisible] = useState<boolean>(false);
  const [selectedOfferIdForTechStops, setSelectedOfferIdForTechStops] = useState<string | null>(null);
  const [availablePlanesOptions, setAvailablePlanesOptions] = useState<any[]>([]);
  const [priceFieldTouched, setPriceFieldTouched] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [positioningAirportCodes, setPositioningAirportCodes] = useState<Record<number, string>>({}); // Map of airportId -> airport code
  const aircraftSelectRef = useRef<HTMLDivElement>(null);
  const aircraftButtonRef = useRef<HTMLDivElement>(null);
  const aircraftSelectComponentRef = useRef<any>(null);

  useEffect(() => {
    // Обновляем localOffers когда меняются offers или когда модалка открывается
    if (visible && offers && offers.length > 0) {
      // Ensure all offers have offerCurrencyId set, prioritizing plane's currency
      // Also ensure all offers have planeId - if missing, we'll look it up when submitting
      const offersWithCurrency = offers.map(offer => {
        // Always use plane's currency (requestedCurrencyId) if available, otherwise use existing or fallback
        const defaultCurrencyId = offer.requestedCurrencyId || offer.offerCurrencyId || company?.currency?.id || userCurrency?.id;
        const defaultCurrencyName = offer.requestedCurrency || offer.offerCurrency || company?.currency?.name || userCurrency?.name || "";
        
        // Update currency to plane's currency if it's available and different from current
        if (offer.requestedCurrencyId && offer.requestedCurrencyId !== offer.offerCurrencyId) {
          return {
            ...offer,
            offerCurrencyId: offer.requestedCurrencyId,
            offerCurrency: offer.requestedCurrency || offer.offerCurrency
          };
        }
        
        // If no currency is set, use defaults
        if (!offer.offerCurrencyId) {
          return {
            ...offer,
            offerCurrencyId: defaultCurrencyId,
            offerCurrency: offer.offerCurrency || defaultCurrencyName
          };
        }
        
        return offer;
      });
      setLocalOffers(offersWithCurrency);
    } else if (visible && (!offers || offers.length === 0)) {
      // Если offers пустой, создаем пустой offer для добавления самолета
      setLocalOffers([]);
    }
  }, [offers, visible, userCurrency, company]);

  // Load positioning airport codes when offers change
  useEffect(() => {
    if (!visible || !localOffers.length) {
      setPositioningAirportCodes({});
      return;
    }

    const loadPositioningAirports = async () => {
      const airportIds = localOffers
        .filter(offer => offer.positioningFromAirportId && offer.positioningFromAirportId > 0)
        .map(offer => offer.positioningFromAirportId!)
        .filter((id, index, self) => self.indexOf(id) === index); // Unique IDs

      if (airportIds.length === 0) {
        setPositioningAirportCodes({});
        return;
      }

      const airports: Record<number, string> = {};
      await Promise.all(
        airportIds.map(async (airportId) => {
          try {
            const req = new AirportApiGetAirportRequest().setAirportid(airportId);
            const airportEntity = (await API.getAirportById(req)).getAirport();
            if (airportEntity) {
              // Prefer ICAO code, fallback to IATA code
              const code = airportEntity.getIcaocode() || airportEntity.getIatacode() || '';
              airports[airportId] = code;
            }
          } catch (error) {
            Logger.error(`[RespondToRequestModal] Failed to load positioning airport ${airportId}:`, error);
          }
        })
      );
      setPositioningAirportCodes(airports);
    };

    loadPositioningAirports();
  }, [localOffers, visible]);

  // Проверяем, что все самолеты имеют статус "accepted" или "declined" (не "pending")
  const allAircraftsInteracted = useMemo(
    () => localOffers.length > 0 && localOffers.every(offer => offer.status === "accepted" || offer.status === "declined"),
    [localOffers]
  );

  // Функция для фильтрации уже добавленных самолетов
  const fetchAvailablePlanes = useMemo(() => {
    const addedAircraftCodes = new Set(localOffers.map(offer => offer.aircraftCode));
    return async (search: string) => {
      const allPlanes = await fetchOwnPlanes(search);
      const filteredPlanes = allPlanes.filter(plane => !addedAircraftCodes.has(plane.label as string));
      // Сохраняем опции для последующего поиска по value
      setAvailablePlanesOptions(filteredPlanes);
      return filteredPlanes;
    };
  }, [localOffers]);

  const handleChangeOffer = (id: string, changes: Partial<LocalOfferState>) => {
    setLocalOffers(prev =>
      prev.map(offer => (offer.id === id ? {...offer, ...changes} : offer))
    );
  };

  const handleStatusChange = (id: string, status: OfferStatus) => {
    setLocalOffers(prev =>
      prev.map(offer => {
        // Если кликнули на тот же самолет с тем же статусом - сбрасываем на pending
        if (offer.id === id && offer.status === status) {
          return {...offer, status: "pending"};
        }
        
        // Если кликнули на другой самолет - меняем только его статус
        if (offer.id === id) {
          // Проверяем, что при попытке принять offer, у него есть aircraftCode или planeId
          if (status === "accepted") {
            const hasAircraftCode = offer.aircraftCode && offer.aircraftCode.trim() !== '';
            const hasPlaneId = offer.planeId && offer.planeId > 0;
            if (!hasAircraftCode && !hasPlaneId) {
              antdMessage.warning(intl.formatMessage({id: 'respondRequestModal.warning.noAircraft', defaultMessage: 'Please select an aircraft before accepting this offer'}));
              return offer; // Не меняем статус
            }
          }
          return {...offer, status};
        }

        // Остальные самолеты остаются без изменений
        return offer;
      })
    );
  };

  // Преобразование строк даты и времени в Dayjs объект (UTC)
  // Строки уже в UTC формате (отформатированы с offset=0 из UTC дат с бекенда)
  // Helper function to get local time string (date and time, or just time if dates match)
  const getLocalTimeDisplay = (utcDate: Dayjs | null, airportOffset: number | null | undefined, utcDateStr: string): { show: boolean; timeOnly: boolean; timeStr: string } => {
    if (!utcDate || airportOffset === null || airportOffset === undefined) {
      return { show: false, timeOnly: false, timeStr: '' };
    }
    
    // Get local date string
    let utcMoment: Dayjs;
    if (utcDate.isUTC()) {
      utcMoment = utcDate;
    } else {
      const dateString = `${utcDate.year()}-${String(utcDate.month() + 1).padStart(2, '0')}-${String(utcDate.date()).padStart(2, '0')} ${String(utcDate.hour()).padStart(2, '0')}:${String(utcDate.minute()).padStart(2, '0')}:${String(utcDate.second()).padStart(2, '0')}`;
      utcMoment = dayjs.utc(dateString, 'YYYY-MM-DD HH:mm:ss');
    }
    
    const localDate = utcMoment.add(airportOffset, 'minute');
    const pad = (num: number) => String(num).padStart(2, '0');
    const localDay = pad(localDate.utc().date());
    const localMonth = pad(localDate.utc().month() + 1);
    const localYear = String(localDate.utc().year()).slice(-2);
    const localHours = pad(localDate.utc().hour());
    const localMinutes = pad(localDate.utc().minute());
    const localDateStr = `${localDay}.${localMonth}.${localYear}`;
    
    // Normalize UTC date string to 2-digit year format for comparison
    // UTC date format can be "DD.MM.YYYY" or "DD.MM.YY", normalize to "DD.MM.YY"
    const utcDateParts = utcDateStr.split('.');
    if (utcDateParts.length === 3) {
      const utcYear = utcDateParts[2].length === 4 ? utcDateParts[2].slice(-2) : utcDateParts[2];
      const normalizedUtcDateStr = `${utcDateParts[0]}.${utcDateParts[1]}.${utcYear}`;
      
      // Compare only dates (format: "DD.MM.YY")
      const datesMatch = localDateStr === normalizedUtcDateStr;
      
      if (datesMatch) {
        // Dates match, show only time
        return { show: true, timeOnly: true, timeStr: `${localHours}:${localMinutes}` };
      } else {
        // Dates differ, show full date and time
        return { show: true, timeOnly: false, timeStr: `${localDay}.${localMonth}.${localYear} ${localHours}:${localMinutes}` };
      }
    }
    
    // If parsing fails, show full date and time
    return { show: true, timeOnly: false, timeStr: `${localDay}.${localMonth}.${localYear} ${localHours}:${localMinutes}` };
  };

  const parseDateTime = (dateStr: string, timeStr: string): Dayjs | null => {
    if (!dateStr || !timeStr) return null;
    // Формат: "08.07.22" -> "08.07.2022"
    const dateParts = dateStr.split('.');
    if (dateParts.length !== 3) return null;
    const year = dateParts[2].length === 2 ? `20${dateParts[2]}` : dateParts[2];
    const dateTimeStr = `${dateParts[0]}.${dateParts[1]}.${year} ${timeStr}`;
    // Парсим строку как UTC время (строки уже в UTC формате)
    return dayjs.utc(dateTimeStr, "DD.MM.YYYY HH:mm");
  };

  // Преобразование Dayjs обратно в строки (UTC)
  const formatDateTime = (date: Dayjs | null): {date: string; time: string} => {
    if (!date) return {date: "", time: ""};
    // Форматируем в UTC (даты должны быть в UTC)
    const utcDate = date.isUTC() ? date : date.utc();
    return {
      date: utcDate.format("DD.MM.YY"),
      time: utcDate.format("HH:mm")
    };
  };


  // Открытие модалки для редактирования даты и времени
  const handleOpenDateTimeModal = (offerId: string) => {
    const offer = localOffers.find(o => o.id === offerId);
    if (offer) {
      const departure = parseDateTime(offer.departureDate, offer.departureTime);
      const arrival = parseDateTime(offer.arrivalDate, offer.arrivalTime);
      if (departure && arrival) {
        setEditingOfferId(offerId);
        setDateTimeRange([departure, arrival]);
        setDateTimeModalVisible(true);
      }
    }
  };

  // Сохранение изменений даты и времени
  const handleSaveDateTime = () => {
    if (!editingOfferId || !dateTimeRange || !dateTimeRange[0] || !dateTimeRange[1]) return;
    
    // DateAndTimeRangePicker может возвращать значения в локальном времени,
    // но мы интерпретируем выбранное пользователем время как UTC
    // (пользователь вводит время в UTC согласно метке "(UTC)" в модалке)
    const departure = formatDateTime(dateTimeRange[0]);
    const arrival = formatDateTime(dateTimeRange[1]);
    
    handleChangeOffer(editingOfferId, {
      departureDate: departure.date,
      departureTime: departure.time,
      arrivalDate: arrival.date,
      arrivalTime: arrival.time
    });
    
    setDateTimeModalVisible(false);
    setEditingOfferId(null);
    setDateTimeRange(null);
  };

  // Закрытие модалки без сохранения
  const handleCloseDateTimeModal = () => {
    setDateTimeModalVisible(false);
    setEditingOfferId(null);
    setDateTimeRange(null);
  };

  const handleAddAircraft = (selectedValue: any) => {
    if (!selectedValue || Array.isArray(selectedValue)) {
      setSelectedAircraftValue(undefined);
      return;
    }
    
    // Извлекаем данные из selectedValue
    // Ant Design Select может вернуть либо объект опции, либо только value
    let aircraftCode: string | undefined;
    let aircraftId: number | undefined;
    
    if (selectedValue && typeof selectedValue === 'object' && 'label' in selectedValue && 'value' in selectedValue) {
      // Это объект опции с label и value
      aircraftCode = selectedValue.label as string;
      aircraftId = selectedValue.value as number;
    } else if (selectedValue && typeof selectedValue === 'object' && 'value' in selectedValue) {
      // Это может быть только value, нужно найти опцию по value
      const foundOption = availablePlanesOptions.find(option => option.value === selectedValue.value);
      if (foundOption) {
        aircraftCode = foundOption.label as string;
        aircraftId = foundOption.value as number;
      }
    } else if (typeof selectedValue === 'number' || (typeof selectedValue === 'string' && !isNaN(Number(selectedValue)))) {
      // Это может быть просто value (number), нужно найти опцию
      const value = typeof selectedValue === 'number' ? selectedValue : Number(selectedValue);
      const foundOption = availablePlanesOptions.find(option => option.value === value);
      if (foundOption) {
        aircraftCode = foundOption.label as string;
        aircraftId = foundOption.value as number;
      }
    }
    
    // Проверяем, что мы получили валидные данные
    if (!aircraftCode || !aircraftId) {
      console.error('Could not extract aircraft code or id from selectedValue:', selectedValue, 'availableOptions:', availablePlanesOptions);
      setSelectedAircraftValue(undefined);
      return;
    }
    
    // Проверяем, что aircraftCode не пустой
    if (aircraftCode.trim() === '') {
      console.error('Aircraft code is empty:', selectedValue);
      setSelectedAircraftValue(undefined);
      return;
    }
    
    // Проверяем, не добавлен ли уже этот самолет
    const isAlreadyAdded = localOffers.some(offer => offer.aircraftCode === aircraftCode);
    if (isAlreadyAdded) {
      antdMessage.warning(intl.formatMessage({id: 'respondRequestModal.warning.aircraftAlreadyAdded', defaultMessage: 'This aircraft is already added'}));
      setSelectedAircraftValue(undefined);
      return;
    }
    
    // Проверяем, есть ли пустой offer (без aircraftCode и planeId), который можно обновить
    const emptyOffer = localOffers.find(offer => 
      (!offer.aircraftCode || offer.aircraftCode.trim() === '') && 
      (!offer.planeId || offer.planeId <= 0)
    );
    
    if (emptyOffer) {
      // Обновляем существующий пустой offer вместо создания нового
      handleChangeOffer(emptyOffer.id, {
        aircraftCode: aircraftCode.trim(),
        planeId: aircraftId
      });
      antdMessage.success(intl.formatMessage({id: 'respondRequestModal.success.aircraftAdded', defaultMessage: 'Aircraft added successfully'}));
    } else {
      // Берем данные из первого существующего offer для создания нового
      const firstOffer = localOffers[0];
      if (!firstOffer) {
        setSelectedAircraftValue(undefined);
        return;
      }
      
      // Создаем новый offer с выбранным самолетом
      // Use plane's currency as default (from requestedCurrencyId)
      const defaultCurrencyId = firstOffer.requestedCurrencyId || firstOffer.offerCurrencyId || company?.currency?.id;
      const defaultCurrencyName = firstOffer.requestedCurrency || firstOffer.offerCurrency || company?.currency?.name || "";
      
      const newOffer: LocalOfferState = {
        id: `${aircraftId}-${Date.now()}`,
        aircraftCode: aircraftCode.trim(), // Убеждаемся, что нет лишних пробелов
        planeId: aircraftId, // Сохраняем planeId для отправки на бекенд
        departureDate: firstOffer.departureDate,
        departureTime: firstOffer.departureTime,
        arrivalDate: firstOffer.arrivalDate,
        arrivalTime: firstOffer.arrivalTime,
        requestedAmount: firstOffer.requestedAmount,
        requestedCurrency: firstOffer.requestedCurrency,
        requestedCurrencyId: firstOffer.requestedCurrencyId,
        brokerRequestedCurrencyId: firstOffer.brokerRequestedCurrencyId,
        offerAmount: "",
        offerCurrency: defaultCurrencyName,
        offerCurrencyId: defaultCurrencyId,
        flightDuration: firstOffer.flightDuration,
        passengers: firstOffer.passengers,
        refuels: firstOffer.refuels,
        status: "pending"
      };
      
      setLocalOffers(prev => [...prev, newOffer]);
    }
    // Очищаем селект после добавления
    setSelectedAircraftValue(undefined);
    // Очищаем сохраненные опции
    setAvailablePlanesOptions([]);
    // Закрываем dropdown
    setAircraftSelectOpen(false);
  };

  const handleAircraftButtonClick = () => {
    setAircraftSelectOpen(true);
    // Программно вызываем клик на селекторе для открытия dropdown
    setTimeout(() => {
      if (aircraftSelectRef.current) {
        const selectElement = aircraftSelectRef.current.querySelector('.ant-select-selector') as HTMLElement;
        if (selectElement) {
          selectElement.click();
        }
      }
    }, 10);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    if (!orderRequestId) {
      setIsSubmitting(false);
      antdMessage.error(intl.formatMessage({id: 'respondRequestModal.error.noOrderRequestId', defaultMessage: 'Ошибка: не указан ID заявки'}));
      return;
    }

    // Фильтруем только принятые предложения (accepted) и проверяем, что у них есть aircraftCode или planeId
    const acceptedOffers = localOffers.filter(offer => {
      if (offer.status !== "accepted") return false;
      // Проверяем, что есть либо aircraftCode, либо planeId (для случаев, когда aircraftCode может быть пустым, но planeId есть)
      const hasAircraftCode = offer.aircraftCode && offer.aircraftCode.trim() !== '';
      const hasPlaneId = offer.planeId && offer.planeId > 0;
      if (!hasAircraftCode && !hasPlaneId) {
        console.warn(`[RespondToRequestModal] Skipping offer without aircraftCode or planeId:`, {
          id: offer.id,
          aircraftCode: offer.aircraftCode,
          planeId: offer.planeId,
          status: offer.status
        });
        return false;
      }
      return true;
    });
    
    console.log(`[RespondToRequestModal] Submit: total offers=${localOffers.length}, accepted offers=${acceptedOffers.length}`, {
      allOffers: localOffers.map(o => ({ id: o.id, status: o.status, aircraftCode: o.aircraftCode, planeId: o.planeId }))
    });
    
    if (acceptedOffers.length === 0) {
      const hasAcceptedButNoAircraft = localOffers.some(offer => {
        if (offer.status !== "accepted") return false;
        const hasAircraftCode = offer.aircraftCode && offer.aircraftCode.trim() !== '';
        const hasPlaneId = offer.planeId && offer.planeId > 0;
        return !hasAircraftCode && !hasPlaneId;
      });
      if (hasAcceptedButNoAircraft) {
        antdMessage.error(intl.formatMessage({id: 'respondRequestModal.error.noAircraftSelected', defaultMessage: 'Please select an aircraft for all accepted offers'}));
      } else {
        antdMessage.error(intl.formatMessage({id: 'respondRequestModal.error.noAcceptedOffers', defaultMessage: 'Необходимо выбрать хотя бы один самолет для предложения'}));
      }
      setIsSubmitting(false);
      return;
    }

    // Преобразуем offers в формат OrderRequestPlaneOfferEntity
    const orderRequestPlanes: (OrderRequestPlaneOfferEntity | null)[] = await Promise.all(acceptedOffers.map(async (offer) => {
      const planeOffer = new OrderRequestPlaneOfferEntity();
      
      // Если есть orderRequestPlaneId, устанавливаем его для обновления существующего предложения
      if (offer.orderRequestPlaneId && offer.orderRequestPlaneId > 0) {
        planeOffer.setOrderrequestplaneid(offer.orderRequestPlaneId);
      }
      
      // Получаем planeId из offer
      let planeId = offer.planeId;
      
      // Если planeId отсутствует, пытаемся найти его по aircraftCode
      if (!planeId || planeId <= 0) {
        // Если есть aircraftCode, используем его для поиска planeId
        if (offer.aircraftCode && offer.aircraftCode.trim() !== '') {
          try {
            // Ищем самолет по registration number
            const planes = await fetchOwnPlanes(offer.aircraftCode.trim());
            const foundPlane = planes.find(p => p.label === offer.aircraftCode.trim());
            if (foundPlane && foundPlane.value) {
              planeId = foundPlane.value as number;
              Logger.info(`[RespondToRequestModal] Found planeId ${planeId} for aircraft code ${offer.aircraftCode}`);
              // Update the local offer with the found planeId for future use
              handleChangeOffer(offer.id, { planeId });
            } else {
              Logger.error(`[RespondToRequestModal] Could not find planeId for aircraft code: ${offer.aircraftCode}. Available planes:`, planes.map(p => p.label));
              antdMessage.error(intl.formatMessage({id: 'respondRequestModal.error.noPlaneId', defaultMessage: `Error: could not determine aircraft ID for {aircraftCode}`}, {aircraftCode: offer.aircraftCode}));
              return null;
            }
          } catch (error) {
            Logger.error(`[RespondToRequestModal] Error looking up planeId for aircraft code ${offer.aircraftCode}:`, error);
            antdMessage.error(intl.formatMessage({id: 'respondRequestModal.error.noPlaneId', defaultMessage: `Error: could not determine aircraft ID for {aircraftCode}`}, {aircraftCode: offer.aircraftCode}));
            return null;
          }
        } else {
          // Если нет ни planeId, ни aircraftCode, это ошибка
          Logger.error(`[RespondToRequestModal] Missing both planeId and aircraftCode for offer:`, offer);
          antdMessage.error(intl.formatMessage({id: 'respondRequestModal.error.noAircraftCode', defaultMessage: 'Error: aircraft code is missing. Please select an aircraft.'}));
          return null;
        }
      }
      
      planeOffer.setPlaneid(planeId);
      
      // Устанавливаем цену и валюту
      // Check if price is provided (trimmed to handle whitespace-only strings)
      const trimmedAmount = offer.offerAmount?.trim() || "";
      if (!trimmedAmount) {
        // Mark all price fields as touched to show validation errors
        setPriceFieldTouched(prev => {
          const newSet = new Set(prev);
          localOffers.forEach(o => newSet.add(o.id));
          return newSet;
        });
        antdMessage.error(intl.formatMessage(modalMessages.priceRequiredError));
        return null;
      }
      // Validate that the price is a valid positive number
      const numericValue = parseFloat(trimmedAmount);
      if (isNaN(numericValue) || numericValue <= 0) {
        antdMessage.error(intl.formatMessage(modalMessages.invalidPriceError, {aircraftCode: offer.aircraftCode}));
        return null;
      }
      if (!offer.offerCurrencyId) {
        antdMessage.error(intl.formatMessage(modalMessages.noCurrencyError, {aircraftCode: offer.aircraftCode}));
        return null;
      }
      planeOffer.setOfferedprice(offer.offerAmount);
      planeOffer.setOfferedcurrencyid(offer.offerCurrencyId);
      
      // Устанавливаем статус STATUS_OFFERED_BY_OPERATOR (2) для всех accepted offers
      // Это означает, что оператор предлагает этот самолет
      planeOffer.setOrderrequeststatusid(OrderRequestPlaneStatusEnum.STATUS_OFFERED_BY_OPERATOR);
      
      // Устанавливаем даты вылета и прибытия от оператора
      if (offer.departureDate && offer.departureTime) {
        const departure = parseDateTime(offer.departureDate, offer.departureTime);
        if (departure) {
          // Форматируем в формат Y-m-d H:i:s для бекенда
          const availableFromDate = departure.format('YYYY-MM-DD HH:mm:ss');
          planeOffer.setAvailablefromdate(availableFromDate);
        }
      }
      
      if (offer.arrivalDate && offer.arrivalTime) {
        const arrival = parseDateTime(offer.arrivalDate, offer.arrivalTime);
        if (arrival) {
          // Форматируем в формат Y-m-d H:i:s для бекенда
          const availableToDate = arrival.format('YYYY-MM-DD HH:mm:ss');
          planeOffer.setAvailabletodate(availableToDate);
        }
      }
      
      return planeOffer;
    }));
    
    // Filter out null values
    const validOrderRequestPlanes = orderRequestPlanes.filter((plane): plane is OrderRequestPlaneOfferEntity => plane !== null);

    if (validOrderRequestPlanes.length === 0) {
      setIsSubmitting(false);
      return;
    }

    try {
      const req = new BookingApiOperatorSubmitOfferRequest()
        .setOrderrequestid(orderRequestId)
        .setOrderrequestplanesList(validOrderRequestPlanes)
        .setMessage(message);

      Logger.collapsedLog('[operatorSubmitOffer] Sending request', {
        orderRequestId,
        orderRequestPlanes: validOrderRequestPlanes.map(plane => ({
          orderRequestPlaneId: plane.getOrderrequestplaneid(),
          planeId: plane.getPlaneid(),
          offeredPrice: plane.getOfferedprice(),
          offeredCurrencyId: plane.getOfferedcurrencyid(),
          orderRequestStatusId: plane.getOrderrequeststatusid(),
        })),
        message,
        acceptedOffersCount: acceptedOffers.length,
      }, 'aqua');

      const response = await API.operatorSubmitOffer(req);

      Logger.collapsedLog('[operatorSubmitOffer] Response received', {
        isSuccessful: response.getResultresponse()?.getIssuccessful(),
        orderRequestId: response.getOrderrequestid(),
        createdOrderRequestPlaneIds: response.getCreatedorderrequestplaneidsList(),
        updatedOrderRequestPlaneIds: response.getUpdatedorderrequestplaneidsList(),
        errorMessage: response.getErrormessage(),
        resultResponse: response.getResultresponse()?.toObject(),
      }, response.getResultresponse()?.getIssuccessful() ? 'lime' : 'orange');

      if (response.getResultresponse()?.getIssuccessful()) {
        antdMessage.success(intl.formatMessage({id: 'respondRequestModal.success', defaultMessage: 'Предложение успешно отправлено'}));
        const declinedCount = localOffers.filter(o => o.status === 'declined').length;
        const proposedPrices = acceptedOffers
          .map(o => (o.offerAmount && typeof o.offerAmount === 'string' ? parseFloat(o.offerAmount.replace(/,/g, '')) : typeof o.offerAmount === 'number' ? o.offerAmount : NaN))
          .filter(n => !isNaN(n) && n > 0);
        const itinerary = orderRouteOrderRequests.map(r => ({
          airport_from_code: r.orderRoute?.airportFrom?.icaoCode || r.orderRoute?.airportFrom?.iataCode,
          airport_to_code: r.orderRoute?.airportTo?.icaoCode || r.orderRoute?.airportTo?.iataCode,
        }));
        amplitudeEvents.operatorRespondToRequest({
          action: 'approve',
          proposed_count: acceptedOffers.length,
          declined_count: declinedCount,
          itinerary: itinerary.length ? itinerary : undefined,
          min_proposed_price: proposedPrices.length ? Math.min(...proposedPrices) : null,
          max_proposed_price: proposedPrices.length ? Math.max(...proposedPrices) : null,
        });
        onSubmit?.({
          offers: localOffers,
          message,
          confirmed: confirmationChecked
        });
        // Вызываем onSuccess перед закрытием модалки для обновления списка
        onSuccess?.();
        onClose();
      } else {
        const errorCode = response.getErrormessage() || response.getResultresponse()?.getErrormessage();
        const errorMsg = errorCode ? getErrorMessageByCode(errorCode) : intl.formatMessage({id: 'respondRequestModal.error.submit', defaultMessage: 'Ошибка при отправке предложения'});
        Logger.error('[operatorSubmitOffer] Request failed');
        Logger.collapsedLog('[operatorSubmitOffer] Error details', {
          orderRequestId,
          errorMessage: errorMsg,
          errorCode: errorCode,
          response: response.toObject(),
          resultResponse: response.getResultresponse()?.toObject(),
        }, 'red');
        antdMessage.error(errorMsg);
      }
    } catch (error: any) {
      Logger.error('[operatorSubmitOffer] Exception occurred');
      Logger.collapsedLog('[operatorSubmitOffer] Exception details', {
        orderRequestId,
        error: error,
        errorMessage: error?.message,
        errorStack: error?.stack,
        errorName: error?.name,
        fullError: JSON.stringify(error, Object.getOwnPropertyNames(error)),
      }, 'red');
      antdMessage.error(intl.formatMessage({id: 'respondRequestModal.error.submit', defaultMessage: 'Ошибка при отправке предложения'}));
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => {
    setLocalOffers(offers || []);
    setConfirmationChecked(false);
    setMessage("");
    setSelectedAircraftValue(undefined);
    setAircraftSelectOpen(false);
    setDateTimeModalVisible(false);
    setEditingOfferId(null);
    setDateTimeRange(null);
    setTechnicalStopsModalVisible(false);
    setSelectedOfferIdForTechStops(null);
    onClose();
  };

  const modalTitle = intl.formatMessage(modalMessages.respondTitle, {
    request: requestName ?? intl.formatMessage(modalMessages.requestPlaceholder)
  });

  // Calculate requested price display: main price in plane's currency, secondary in user's currency if different
  const getRequestedPriceDisplay = useMemo(() => {
    return (offer: RequestOffer) => {
      // Log requested currency info for debugging
      console.log('[RespondToRequestModal] Requested currency info:', {
        requestedCurrencyId: offer.requestedCurrencyId,
        requestedCurrency: offer.requestedCurrency,
        requestedAmount: offer.requestedAmount,
        brokerRequestedCurrencyId: offer.brokerRequestedCurrencyId,
        userCurrencyId: userCurrency?.id,
        userCurrencyName: userCurrency?.name,
        isCurrencyConverterReady
      });

      if (!offer.requestedAmount || !offer.requestedCurrency) {
        return {
          displayPrice: null,
          displayCurrency: null,
          originalPrice: null,
          originalCurrency: null
        };
      }

      // Parse the broker's requested amount (remove formatting)
      const cleanedAmount = offer.requestedAmount.replace(/\s/g, '').replace(/,/g, '.');
      const brokerRequestedAmount = parseFloat(cleanedAmount);
      
      if (isNaN(brokerRequestedAmount) || brokerRequestedAmount <= 0) {
        return {
          displayPrice: null,
          displayCurrency: null,
          originalPrice: null,
          originalCurrency: null
        };
      }

      const planeCurrencyId = offer.requestedCurrencyId; // Plane's currency (from calculator)
      const brokerCurrencyId = offer.brokerRequestedCurrencyId; // Broker's requested currency
      const userCurrencyId = userCurrency?.id;
      
      // Step 1: Convert broker's price to plane's currency if they differ
      let priceInPlaneCurrency = brokerRequestedAmount;
      if (brokerCurrencyId && planeCurrencyId && brokerCurrencyId !== planeCurrencyId && isCurrencyConverterReady) {
        try {
          priceInPlaneCurrency = convertSync(brokerRequestedAmount, brokerCurrencyId, planeCurrencyId);
          console.log('[RespondToRequestModal] Converted broker price to plane currency:', {
            brokerAmount: brokerRequestedAmount,
            brokerCurrencyId,
            planeCurrencyId,
            priceInPlaneCurrency
          });
        } catch (error) {
          console.error('[RespondToRequestModal] Error converting broker price to plane currency:', error);
        }
      }
      
      // Round price in plane's currency
      const roundedPlanePrice = Math.round(priceInPlaneCurrency * 100) / 100;
      
      // Create currency object for plane's currency
      const planeCurrencyObj: ICurrency | null = planeCurrencyId && offer.requestedCurrency 
        ? { id: planeCurrencyId, name: offer.requestedCurrency }
        : null;
      
      // Step 2: Convert plane's currency price to user's currency if they differ
      let priceInUserCurrency: number | null = null;
      if (userCurrency && isCurrencyConverterReady && planeCurrencyId && userCurrencyId && planeCurrencyId !== userCurrencyId) {
        try {
          priceInUserCurrency = convertSync(priceInPlaneCurrency, planeCurrencyId, userCurrencyId);
          console.log('[RespondToRequestModal] Converted plane price to user currency:', {
            planePrice: priceInPlaneCurrency,
            planeCurrencyId,
            userCurrencyId,
            priceInUserCurrency
          });
        } catch (error) {
          console.error('[RespondToRequestModal] Error converting plane price to user currency:', error);
        }
      }
      
      // Round user currency price if available
      let roundedUserPrice: number | null = null;
      if (priceInUserCurrency !== null) {
        roundedUserPrice = Math.round(priceInUserCurrency * 100) / 100;
      }
      
      // Main price is in plane's currency, secondary is in user's currency (if different)
      return {
        displayPrice: roundedPlanePrice,
        displayCurrency: planeCurrencyObj,
        originalPrice: roundedUserPrice,
        originalCurrency: roundedUserPrice && userCurrency ? userCurrency : null
      };
    };
  }, [userCurrency, isCurrencyConverterReady, convertSync]);

  return (
    <>
    <StyledModal
      open={visible}
      footer={null}
      onCancel={closeModal}
      centered
      maskClosable={false}
      closable={false}
      width={isMobile ? undefined : 1080}
      destroyOnClose
    >
      <Content>
        <Header>
          <div>
            <Title>{modalTitle}</Title>
          </div>
          <CloseButton onClick={closeModal} aria-label={intl.formatMessage(CommonButtonMessages.close)}>
            <CloseOutlined />
          </CloseButton>
        </Header>

        {/* Route display at the top */}
        {orderRouteOrderRequests && orderRouteOrderRequests.length > 0 && (
          <Section>
            <SectionTitle>{intl.formatMessage({id: 'respondRequestModal.routeTitle', defaultMessage: 'Requested route and departure time'})}</SectionTitle>
            <FlightCard>
              <RouteLine style={{justifyContent: 'center', padding: '16px'}}>
                {orderRouteOrderRequests.map((routeRequest, routeIndex) => {
                  const route = routeRequest.orderRoute;
                  const requestedDepartureDate = route.departureDate; // This is now the requested_start from backend
                  const airportFrom = route.airportFrom;
                  const airportTo = route.airportTo;
                  const isLastLeg = routeIndex === orderRouteOrderRequests.length - 1;
                  
                  // Get departure date for the next leg (if this airport is an intermediate stop)
                  const nextLeg = !isLastLeg ? orderRouteOrderRequests[routeIndex + 1] : null;
                  const nextLegDepartureDate = nextLeg?.orderRoute?.departureDate;
                  
                  // Format requested departure date/time in UTC for this leg (first airport)
                  const requestedDateStr = requestedDepartureDate ? formatDate(requestedDepartureDate, 0, "date") : '';
                  const requestedTimeStr = requestedDepartureDate ? formatDate(requestedDepartureDate, 0, "time") : '';
                  
                  // Format requested departure date/time for next leg (intermediate airports)
                  const nextLegDateStr = nextLegDepartureDate ? formatDate(nextLegDepartureDate, 0, "date") : '';
                  const nextLegTimeStr = nextLegDepartureDate ? formatDate(nextLegDepartureDate, 0, "time") : '';
                  
                  // Format local time for origin airport of this leg
                  const localTimeFrom = requestedDepartureDate && airportFrom?.timezone?.offset !== undefined
                    ? formatDate(requestedDepartureDate, airportFrom.timezone.offset, "time")
                    : null;
                  
                  // Format local time for intermediate airport (when departing from it in next leg)
                  const localTimeTo = nextLegDepartureDate && airportTo?.timezone?.offset !== undefined
                    ? formatDate(nextLegDepartureDate, airportTo.timezone.offset, "time")
                    : null;
                  
                  return (
                    <React.Fragment key={routeIndex}>
                      {/* Show origin airport only for first leg */}
                      {routeIndex === 0 && (
                        <RouteAirportCode>
                          <RouteAirportCodeText>{airportFrom?.icaoCode || airportFrom?.iataCode || ''}</RouteAirportCodeText>
                          {requestedDepartureDate && (
                            <RouteDateTimeInfo>
                              <div>{requestedDateStr} {requestedTimeStr} UTC</div>
                              {localTimeFrom && (
                                <div>{localTimeFrom} Local time</div>
                              )}
                            </RouteDateTimeInfo>
                          )}
                        </RouteAirportCode>
                      )}
                      
                      <RouteArrowWithPax>
                        <RouteArrowWrapper>
                          <RouteArrowIcon/>
                        </RouteArrowWrapper>
                        <RoutePaxLabel>
                          {route.pax ?? 0} {intl.formatMessage({id: 'newTripRequestPage.pax', defaultMessage: 'PAX'})}
                        </RoutePaxLabel>
                      </RouteArrowWithPax>
                      
                      {/* Show destination airport - only show departure time if it's an intermediate stop (has next leg) */}
                      <RouteAirportCode>
                        <RouteAirportCodeText>{airportTo?.icaoCode || airportTo?.iataCode || ''}</RouteAirportCodeText>
                        {!isLastLeg && nextLegDepartureDate && (
                          <RouteDateTimeInfo>
                            <div>{nextLegDateStr} {nextLegTimeStr} UTC</div>
                            {localTimeTo && (
                              <div>{localTimeTo} Local time</div>
                            )}
                          </RouteDateTimeInfo>
                        )}
                      </RouteAirportCode>
                    </React.Fragment>
                  );
                })}
              </RouteLine>
            </FlightCard>
          </Section>
        )}

        <Section>
          <SectionTitle>{intl.formatMessage(modalMessages.sectionTitle)}</SectionTitle>
          <FlightCard>
            {localOffers.length === 0 ? (
              <div style={{ padding: '16px', textAlign: 'center', color: '#808080' }}>
                {intl.formatMessage({ id: 'respondRequestModal.noOffers', defaultMessage: 'Нет данных о предложениях' })}
              </div>
            ) : (
              localOffers.map((offer, index) => (
              <FlightRow key={offer.id} $withBorder={index < localOffers.length - 1}>
                <FlightRowContent>
                <FlightCell $maxWidth={100} $mobileFullWidth>
                  <CellLabel>{intl.formatMessage(modalMessages.aircraftLabel)}</CellLabel>
                  <CellContent>
                    <CellValue>{offer.aircraftCode}</CellValue>
                  </CellContent>
                </FlightCell>

                <FlightCell $mobileFullWidth style={{ flex: '1 1 auto', minWidth: 0 }}>
                  <CellLabel>{intl.formatMessage(modalMessages.routeLabel)}</CellLabel>
                  <CellContent style={{flexDirection: 'column', alignItems: 'flex-start', gap: '8px'}}>
                    {(() => {
                      const departureUtc = parseDateTime(offer.departureDate, offer.departureTime);
                      const arrivalUtc = parseDateTime(offer.arrivalDate, offer.arrivalTime);
                      
                      const departureLocal = getLocalTimeDisplay(departureUtc, airportFrom?.timezone?.offset, offer.departureDate);
                      const arrivalLocal = getLocalTimeDisplay(arrivalUtc, airportTo?.timezone?.offset, offer.arrivalDate);
                      
                      return (
                        <>
                          <RouteRowWithLocalTime>
                            {readOnly ? (
                              <>
                                <RouteSegmentWithLocalTime>
                                  <RouteHighlight>
                                    <span>{offer.departureDate}</span> <span>{offer.departureTime}</span>
                                  </RouteHighlight>
                                  {departureLocal.show && (
                                    <LocalTimeRow>
                                      Local: {departureLocal.timeStr}
                                    </LocalTimeRow>
                                  )}
                                </RouteSegmentWithLocalTime>
                                <span>–</span>
                                <RouteSegmentWithLocalTime>
                                  <RouteHighlight>
                                    <span>{offer.arrivalDate}</span> <span>{offer.arrivalTime}</span>
                                  </RouteHighlight>
                                  {arrivalLocal.show && (
                                    <LocalTimeRow>
                                      Local: {arrivalLocal.timeStr}
                                    </LocalTimeRow>
                                  )}
                                </RouteSegmentWithLocalTime>
                              </>
                            ) : (
                              <>
                                <RouteSegmentWithLocalTime>
                                  <ClickableRouteHighlight onClick={() => handleOpenDateTimeModal(offer.id)}>
                                    <RouteHighlight>
                                      <span>{offer.departureDate}</span> <span>{offer.departureTime}</span>
                                    </RouteHighlight>
                                  </ClickableRouteHighlight>
                                  {departureLocal.show && (
                                    <LocalTimeRow>
                                      Local: {departureLocal.timeStr}
                                    </LocalTimeRow>
                                  )}
                                </RouteSegmentWithLocalTime>
                                <span>–</span>
                                <RouteSegmentWithLocalTime>
                                  <ClickableRouteHighlight onClick={() => handleOpenDateTimeModal(offer.id)}>
                                    <RouteHighlight>
                                      <span>{offer.arrivalDate}</span> <span>{offer.arrivalTime}</span>
                                    </RouteHighlight>
                                  </ClickableRouteHighlight>
                                  {arrivalLocal.show && (
                                    <LocalTimeRow>
                                      Local: {arrivalLocal.timeStr}
                                    </LocalTimeRow>
                                  )}
                                </RouteSegmentWithLocalTime>
                              </>
                            )}
                          </RouteRowWithLocalTime>
                        </>
                      );
                    })()}
                  </CellContent>
                </FlightCell>

                <FlightCell $minWidth={80} $mobileThirdWidth>
                  <CellLabel>{intl.formatMessage(modalMessages.requestedLabel)}</CellLabel>
                  <CellContent>
                    {(() => {
                      const priceDisplay = getRequestedPriceDisplay(offer);
                      return (
                        <RequestedPriceContainer>
                          <RequestedValue>
                            {priceDisplay.displayPrice !== null && priceDisplay.displayCurrency ? (
                              <>
                                {DisplayFlyPrice(priceDisplay.displayPrice, priceDisplay.displayCurrency)}
                                <Tooltip 
                                  title={(() => {
                                    const baseTooltip = intl.formatMessage(modalMessages.requestedTooltip);
                                    if (offer.positioningDurationMinutes && offer.positioningDurationMinutes > 0 && offer.positioningFromAirportId) {
                                      const airportCode = positioningAirportCodes[offer.positioningFromAirportId] || '';
                                      const hours = Math.floor(offer.positioningDurationMinutes / 60);
                                      const minutes = offer.positioningDurationMinutes % 60;
                                      const timeStr = `${hours}:${String(minutes).padStart(2, '0')}`;
                                      const distance = offer.positioningDistanceKm 
                                        ? DisplayFlyDistance(offer.positioningDistanceKm, userDistanceUnit)
                                        : '';
                                      const positioningNote = intl.formatMessage(modalMessages.positioningNote, {
                                        airportCode: airportCode || `[${offer.positioningFromAirportId}]`,
                                        time: timeStr,
                                        distance: distance
                                      });
                                      return (
                                        <>
                                          {baseTooltip}
                                          <br />
                                          {positioningNote}
                                        </>
                                      );
                                    }
                                    return baseTooltip;
                                  })()}
                                >
                                  <InfoCircleOutlined />
                                </Tooltip>
                              </>
                            ) : (
                              <span>{offer.requestedAmount || '—'}</span>
                            )}
                          </RequestedValue>
                          {/* Show original currency if different from user's currency */}
                          {priceDisplay.originalPrice !== null && priceDisplay.originalCurrency && (
                            <RequestedPriceOriginal>
                              {DisplayFlyPrice(priceDisplay.originalPrice, priceDisplay.originalCurrency)}
                            </RequestedPriceOriginal>
                          )}
                        </RequestedPriceContainer>
                      );
                    })()}
                  </CellContent>
                </FlightCell>

                <FlightCell $width={72} $mobileThirdWidth>
                  <CellLabel>{intl.formatMessage(modalMessages.priceLabel)}</CellLabel>
                  <CellContent>
                    {readOnly ? (
                      <CellValue>{offer.offerAmount || "-"}</CellValue>
                    ) : (
                      <StyledInput
                        value={offer.offerAmount || ""}
                        onChange={event => {
                          // Allow numbers and decimals with a point
                          const value = event.target.value;
                          // Allow empty string, numbers, and one decimal point
                          // Regex allows: digits, optional decimal point, optional more digits
                          // Examples: "123", "123.45", ".5", "0.5"
                          if (value === "" || /^\d*\.?\d*$/.test(value)) {
                            handleChangeOffer(offer.id, {offerAmount: value});
                          }
                        }}
                        onBlur={event => {
                          // Mark field as touched
                          setPriceFieldTouched(prev => new Set(prev).add(offer.id));
                          // Trim whitespace on blur
                          const value = event.target.value.trim();
                          if (value !== offer.offerAmount) {
                            handleChangeOffer(offer.id, {offerAmount: value});
                          }
                        }}
                        placeholder=""
                        status={priceFieldTouched.has(offer.id) && (!offer.offerAmount || offer.offerAmount.trim() === "") ? "error" : undefined}
                        type="text"
                        inputMode="decimal"
                      />
                    )}
                  </CellContent>
                </FlightCell>

                <FlightCell $width={80} $mobileThirdWidth>
                  <CellLabel>{intl.formatMessage(modalMessages.currencyLabel)}</CellLabel>
                  <CellContent>
                    {readOnly ? (
                      <CellValue>{offer.offerCurrency || "-"}</CellValue>
                    ) : (
                      <StyledDebounceSelect
                        fetchOptions={fetchCurrencies}
                        defaultOptionsFetchCallback={fetchCurrencies}
                        labelInValue={true}
                        defaultOptions={
                          offer.offerCurrencyId 
                            ? [getInitialCurrencyForSelect({id: offer.offerCurrencyId, name: offer.offerCurrency} as ICurrency)]
                            : offer.requestedCurrencyId
                              ? [getInitialCurrencyForSelect({id: offer.requestedCurrencyId, name: offer.requestedCurrency} as ICurrency)]
                              : company?.currency 
                                ? [getInitialCurrencyForSelect(company.currency)]
                                : []
                        }
                        value={
                          offer.offerCurrencyId 
                            ? getInitialCurrencyForSelect({id: offer.offerCurrencyId, name: offer.offerCurrency} as ICurrency)
                            : offer.requestedCurrencyId
                              ? getInitialCurrencyForSelect({id: offer.requestedCurrencyId, name: offer.requestedCurrency} as ICurrency)
                              : company?.currency 
                                ? getInitialCurrencyForSelect(company.currency)
                                : undefined
                        }
                        onChange={async (selectedValue) => {
                          if (selectedValue && !Array.isArray(selectedValue)) {
                            let currencyId: number;
                            let currencyName: string;
                            
                            // Если selectedValue - число (ID валюты), нужно найти валюту по ID
                            if (typeof selectedValue === 'number') {
                              currencyId = selectedValue;
                              // Загружаем валюты и находим название по ID
                              const currencies = await fetchCurrencies();
                              const foundCurrency = currencies.find(c => c.value === currencyId);
                              currencyName = foundCurrency?.label || '';
                            } 
                            // Если selectedValue - объект с value и label
                            else if (typeof selectedValue === 'object' && selectedValue !== null) {
                              currencyId = (selectedValue as any).value as number;
                              currencyName = (selectedValue as any).label as string;
                            } else {
                              return;
                            }
                            
                            if (currencyId && currencyName) {
                              // Получаем старую валюту и сумму для конвертации
                              const oldCurrencyId = offer.offerCurrencyId;
                              const oldAmountStr = offer.offerAmount;
                              
                              // Если есть старая валюта, старая сумма и новая валюта отличается - конвертируем
                              let newAmount = oldAmountStr;
                              if (oldCurrencyId && oldAmountStr && oldCurrencyId !== currencyId && isCurrencyConverterReady) {
                                // Парсим сумму (убираем пробелы, запятые, заменяем на точку для десятичных)
                                const cleanedAmount = oldAmountStr.replace(/\s/g, '').replace(/,/g, '.');
                                const parsedAmount = parseFloat(cleanedAmount);
                                
                                if (!isNaN(parsedAmount) && parsedAmount > 0) {
                                  // Конвертируем сумму
                                  const convertedAmount = convertSync(parsedAmount, oldCurrencyId, currencyId);
                                  
                                  // Форматируем конвертированную сумму (округляем до 2 знаков после запятой)
                                  const roundedAmount = Math.round(convertedAmount * 100) / 100;
                                  
                                  // Форматируем число для отображения (убираем лишние нули, но сохраняем до 2 знаков)
                                  newAmount = roundedAmount.toFixed(2).replace(/\.?0+$/, '');
                                }
                              }
                              
                              handleChangeOffer(offer.id, {
                                offerCurrencyId: currencyId,
                                offerCurrency: currencyName,
                                offerAmount: newAmount
                              });
                            }
                          } else {
                            handleChangeOffer(offer.id, {
                              offerCurrencyId: undefined,
                              offerCurrency: ""
                            });
                          }
                        }}
                        placeholder="-"
                      />
                    )}
                  </CellContent>
                </FlightCell>

                <FlightCell $width={65} $mobileThirdWidth>
                  <CellLabel>{intl.formatMessage(modalMessages.durationLabel)}</CellLabel>
                  <CellContent>
                    <CellValue>{offer.flightDuration}</CellValue>
                  </CellContent>
                </FlightCell>

                <FlightCell $width={65} $mobileThirdWidth>
                  <CellLabel>{intl.formatMessage(modalMessages.passengersLabel)}</CellLabel>
                  <CellContent>
                    <IconValue>
                      <PassengerIconWrapper>
                        <PassengerIcon />
                      </PassengerIconWrapper>
                      <CellValue>{offer.passengers}</CellValue>
                    </IconValue>
                  </CellContent>
                </FlightCell>

                <FlightCell $width={65} $mobileThirdWidth>
                  <CellLabel>{intl.formatMessage(modalMessages.refuelingsLabel)}</CellLabel>
                  <CellContent>
                    <IconValue 
                      style={{ cursor: readOnly ? 'default' : 'pointer' }}
                      onClick={readOnly ? undefined : () => {
                        setSelectedOfferIdForTechStops(offer.id);
                        setTechnicalStopsModalVisible(true);
                      }}
                    >
                      <FuelIconWrapper>
                        <FuelIcon />
                      </FuelIconWrapper>
                      <RefuelValue>{offer.refuels}</RefuelValue>
                    </IconValue>
                  </CellContent>
                </FlightCell>

                <FlightCell $mobileFullWidth>
                  <CellLabel>&nbsp;</CellLabel>
                  <CellContent>
                    <ActionsCell>
                      <StatusButton
                        type="button"
                        $variant="accept"
                        $active={offer.status === "accepted"}
                        disabled={readOnly || (!offer.aircraftCode || offer.aircraftCode.trim() === '') && (!offer.planeId || offer.planeId <= 0)}
                        onClick={readOnly ? undefined : () => handleStatusChange(offer.id, "accepted")}
                      >
                        <StyledAcceptIcon $active={offer.status === "accepted"} />
                      </StatusButton>
                      <StatusButton
                        type="button"
                        $variant="decline"
                        $active={offer.status === "declined"}
                        disabled={readOnly}
                        onClick={readOnly ? undefined : () => handleStatusChange(offer.id, "declined")}
                      >
                        <CloseSymbol />
                      </StatusButton>
                    </ActionsCell>
                  </CellContent>
                </FlightCell>
                </FlightRowContent>
                
                <WarningText style={{ width: '100%', marginTop: '8px', paddingLeft: '0' }}>
                  <WarningOutlined />
                  {intl.formatMessage(modalMessages.warningText)}
                </WarningText>
              </FlightRow>
            ))
            )}
          </FlightCard>
        </Section>

        {!readOnly && (
          <FormSection>
            <CheckboxRow>
              <Checkbox checked={confirmationChecked} onChange={event => setConfirmationChecked(event.target.checked)}>
                {intl.formatMessage(modalMessages.confirmationText)}
              </Checkbox>
            </CheckboxRow>

            {brokerComment && (
              <WelcomeMessageSection>
                <WelcomeMessageLabel>{intl.formatMessage({id: 'respondRequestModal.welcomeMessageLabel', defaultMessage: 'Сообщение от брокера'})}</WelcomeMessageLabel>
                <WelcomeMessageBox>
                  {brokerComment}
                </WelcomeMessageBox>
              </WelcomeMessageSection>
            )}

            <TextareaSection>
              <Label>{intl.formatMessage(modalMessages.letterLabel)}</Label>
              <StyledTextArea
                value={message}
                onChange={event => setMessage(event.target.value)}
                placeholder={intl.formatMessage(modalMessages.letterPlaceholder)}
                autoSize={{minRows: 4, maxRows: 6}}
              />
            </TextareaSection>
          </FormSection>
        )}

        <Footer>
          {!readOnly && (
            <AircraftSelectWrapper ref={aircraftButtonRef}>
              <StyledAddAircraftButton 
                type="default" 
                heightSize="m"
                onClick={handleAircraftButtonClick}
              >
                {intl.formatMessage(modalMessages.addAircraft)}
              </StyledAddAircraftButton>
              <HiddenAircraftSelect ref={aircraftSelectRef}>
                <DebounceSelect
                  fetchOptions={fetchAvailablePlanes}
                  placeholder={intl.formatMessage(modalMessages.addAircraft)}
                  onChange={handleAddAircraft}
                  value={selectedAircraftValue}
                  allowClear={false}
                  open={aircraftSelectOpen}
                  onDropdownVisibleChange={setAircraftSelectOpen}
                  style={{width: '100%'}}
                  placement="bottomLeft"
                  labelInValue={true}
                  getPopupContainer={(trigger) => {
                    return aircraftButtonRef.current || document.body;
                  }}
                />
              </HiddenAircraftSelect>
            </AircraftSelectWrapper>
          )}
          <FooterActions>
            <CustomButton type="default" onClick={closeModal} heightSize="m">
              {intl.formatMessage(CommonButtonMessages.cancel)}
            </CustomButton>
            {!readOnly && (
              <Tooltip 
                title={
                  !allAircraftsInteracted 
                    ? intl.formatMessage(modalMessages.interactWithAllAircraftTooltip)
                    : intl.formatMessage(modalMessages.respondTooltip)
                }
              >
                <span>
                  <CustomButton
                    type="primary"
                    onClick={handleSubmit}
                    heightSize="m"
                    disabled={!confirmationChecked || !allAircraftsInteracted}
                  >
                    {intl.formatMessage(CommonButtonMessages.respond)}
                  </CustomButton>
                </span>
              </Tooltip>
            )}
          </FooterActions>
        </Footer>
      </Content>
    </StyledModal>
    
    <>
      <DateTimeModalStyled
        title={intl.formatMessage(modalMessages.dateTimeModalTitle)}
        open={dateTimeModalVisible}
        onCancel={handleCloseDateTimeModal}
        footer={null}
        centered
        width={isMobile ? undefined : 666}
        wrapClassName="respond-request-date-time-modal"
      >
      <DateTimeModalContent>
        <DateRangeWithLocalTime>
          <DatePickersRow>
            <DatePickerWithLocalTime>
              <DateAndTimePicker
                value={dateTimeRange?.[0] || null}
                onChange={(date: Dayjs | null, dateString: string) => {
                  if (date) {
                    setDateTimeRange([date, dateTimeRange?.[1] || null]);
                  } else {
                    setDateTimeRange([null, dateTimeRange?.[1] || null]);
                  }
                }}
                format="DD.MM.YYYY"
                inputReadOnly={false}
                placement="bottomLeft"
                getPopupContainer={(trigger: HTMLElement) => trigger.parentNode as HTMLElement}
                dateOnFullRow={isMobile}
              />
              <div style={{marginTop: '2px', display: 'flex', justifyContent: 'flex-end'}}>
                <LocalTimeDisplay
                  utcDate={dateTimeRange?.[0] || null}
                  airportOffset={airportFrom?.timezone?.offset}
                />
              </div>
            </DatePickerWithLocalTime>
            <RangeSeparator>—</RangeSeparator>
            <DatePickerWithLocalTime>
              <DateAndTimePicker
                value={dateTimeRange?.[1] || null}
                onChange={(date: Dayjs | null, dateString: string) => {
                  if (date) {
                    setDateTimeRange([dateTimeRange?.[0] || null, date]);
                  } else {
                    setDateTimeRange([dateTimeRange?.[0] || null, null]);
                  }
                }}
                format="DD.MM.YYYY"
                inputReadOnly={false}
                placement="bottomLeft"
                getPopupContainer={(trigger: HTMLElement) => trigger.parentNode as HTMLElement}
                dateOnFullRow={isMobile}
              />
              <div style={{marginTop: '2px', display: 'flex', justifyContent: 'flex-end'}}>
                <LocalTimeDisplay
                  utcDate={dateTimeRange?.[1] || null}
                  airportOffset={airportTo?.timezone?.offset}
                />
              </div>
            </DatePickerWithLocalTime>
          </DatePickersRow>
        </DateRangeWithLocalTime>
        <DateTimeModalFooter>
          <CustomButton type="default" onClick={handleCloseDateTimeModal} heightSize="m">
            {intl.formatMessage(CommonButtonMessages.cancel)}
          </CustomButton>
          <CustomButton
            type="primary"
            onClick={handleSaveDateTime}
            heightSize="m"
            disabled={!dateTimeRange || !dateTimeRange[0] || !dateTimeRange[1]}
          >
            {intl.formatMessage(CommonButtonMessages.save)}
          </CustomButton>
        </DateTimeModalFooter>
      </DateTimeModalContent>
      </DateTimeModalStyled>
    </>

    <TechnicalStopsModal
      visible={technicalStopsModalVisible}
      onClose={() => {
        setTechnicalStopsModalVisible(false);
        setSelectedOfferIdForTechStops(null);
      }}
      onSave={(stops) => {
        // TODO: Сохранить технические остановки для выбранного offer
        console.log('Technical stops saved for offer:', selectedOfferIdForTechStops, stops);
      }}
    />
    </>
  );
};

// Styled-component с несколькими уровнями специфичности для перебивания стилей Ant Design
// Используем разные селекторы для максимальной совместимости с порталом модального окна
const DateTimeModalStyled = styled(CustomModal)`
  @media (max-width: ${mediaScreenSizes.mobile}) {
    display: flex;
    flex-direction: column;
  }

  @media (min-width: ${mediaScreenSizes.mobile + 1}) {
    /* Уровень 1: Стили для самого компонента */
    &.ant-modal {
      min-width: 666px !important;
      width: 666px !important;
      max-width: 666px !important;
    }
    
    /* Уровень 2: Стили через wrapClassName (модалка рендерится в портале) */
    /* Используем селекторы для body, чтобы применить стили к модальному окну в портале */
    body & {
      min-width: 666px !important;
      width: 666px !important;
      max-width: 666px !important;
    }
    
    /* Уровень 3: Стили через wrapClassName с более специфичным селектором */
    /* Используем уникальный класс respond-request-date-time-modal, чтобы не затронуть другие модалки */
    body .ant-modal-wrap.respond-request-date-time-modal & {
      min-width: 666px !important;
      width: 666px !important;
      max-width: 666px !important;
    }
    
    /* Уровень 4: Прямой селектор для div с role="dialog" */
    body .ant-modal-wrap.respond-request-date-time-modal .ant-modal {
      min-width: 666px !important;
      width: 666px !important;
      max-width: 666px !important;
    }
    
    /* Уровень 5: Самый специфичный селектор для перебивания дефолтных стилей Ant Design */
    body .ant-modal-wrap.respond-request-date-time-modal .ant-modal-root .ant-modal {
      min-width: 666px !important;
      width: 666px !important;
      max-width: 666px !important;
    }
  }
`;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 20px;
    padding: 32px;
    
    @media (max-width: ${mediaScreenSizes.mobile}) {
      border-radius: 12px;
      padding: 16px;
    }
  }

  .ant-modal-body {
    padding: 0;
  }

  .ant-modal-close {
    display: none;
  }
  
  @media (max-width: ${mediaScreenSizes.mobile}) {
    .ant-modal {
      margin: 0;
      max-width: 100vw;
      padding: 0;
    }
    
    .ant-modal-content {
      border-radius: 12px 12px 0 0;
    }
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  
  @media (max-width: ${mediaScreenSizes.mobile}) {
    gap: 16px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  line-height: 1.2;
  color: #000;
  
  @media (max-width: ${mediaScreenSizes.mobile}) {
    font-size: 18px;
  }
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #000;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.06);
  }
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.2;
  color: #000;
  
  @media (max-width: ${mediaScreenSizes.mobile}) {
    font-size: 16px;
  }
`;

const FlightCard = styled.div`
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow-x: auto;
`;

const FlightRow = styled.div<{ $withBorder: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  min-width: min-content;

  ${({$withBorder}) => $withBorder && "border-bottom: 1px solid #d9d9d9;"}
  
  @media (max-width: ${mediaScreenSizes.mobile}) {
    gap: 12px;
    padding: 12px;
  }
`;

const FlightRowContent = styled.div`
  display: flex;
  gap: 16px;
  align-items: stretch;
  flex-wrap: nowrap;
  width: 100%;
  
  @media (max-width: ${mediaScreenSizes.mobile}) {
    flex-wrap: wrap;
    gap: 12px;
  }
`;

const FlightCell = styled.div<{ $width?: number; $maxWidth?: number; $flex?: boolean; $mobileFullWidth?: boolean; $mobileHalfWidth?: boolean; $mobileThirdWidth?: boolean; $minWidth?: number }>`
  display: flex;
  flex-direction: column;
  ${({$width, $minWidth, $maxWidth}) => {
    if ($minWidth) {
      return `min-width: ${$minWidth}px; flex: 0 0 auto;`;
    }
    if ($maxWidth) {
      return `max-width: ${$maxWidth}px; flex: 0 1 auto;`;
    }
    return $width ? `width: ${$width}px; flex: 0 0 ${$width}px;` : "";
  }}
  ${({$flex}) => $flex ? "flex: 1 1 0;" : ""}
  
  @media (max-width: ${mediaScreenSizes.mobile}) {
    ${({$mobileFullWidth}) => $mobileFullWidth ? `
      width: 100% !important;
      flex: 1 1 100% !important;
    ` : ""}
    ${({$mobileHalfWidth}) => $mobileHalfWidth ? `
      width: calc(50% - 6px) !important;
      flex: 0 0 calc(50% - 6px) !important;
    ` : ""}
    ${({$mobileThirdWidth}) => $mobileThirdWidth ? `
      width: calc(33.333% - 8px) !important;
      flex: 0 0 calc(33.333% - 8px) !important;
    ` : ""}
  }
`;

const CellLabel = styled.span`
  font-size: 11px;
  color: #808080;
  margin-bottom: 8px;
`;

const CellContent = styled.div`
  display: flex;
  align-items: flex-start;
  flex: 1;
`;

const CellValue = styled.span`
  font-size: 14px;
  color: #000;
`;

const RouteRowWithLocalTime = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
`;

const RouteSegmentWithLocalTime = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const LocalTimeRow = styled.div`
  font-size: 12px;
  color: #999999;
  padding-left: 4px;
  line-height: 1.4;
`;

const ClickableRouteHighlight = styled.div`
  cursor: pointer;
  
  &:hover {
    opacity: 0.7;
  }
`;

const RouteRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #000;
  flex-wrap: nowrap;
`;

const ClickableRouteRow = styled(RouteRow)`
  cursor: pointer;
  
  &:hover {
    opacity: 0.7;
  }
`;

const RouteSegment = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const RouteHighlight = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-bottom: 1px dotted #000;
  padding-bottom: 2px;
`;

const WarningText = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 11px;
  color: #808080;

  .anticon {
    color: #808080;
    font-size: 12px;
    margin-top: 2px;
  }
`;

const RequestedValue = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 600;
  color: #1890ff;

  .anticon {
    font-size: 14px;
    color: #1890ff;
  }
`;

const CurrencySymbol = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #1890ff;
  margin-right: 2px;
`;

const StyledInput = styled(Input)`
  height: 36px;
  border-radius: 4px;
`;

const StyledDebounceSelect = styled(DebounceSelect)`
  width: 100%;
  max-width: 100%;
  
  .ant-select-selector {
    height: 36px !important;
    border-radius: 4px !important;
    display: flex;
    align-items: center;
    max-width: 100%;
  }
`;

const IconValue = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const PassengerIconWrapper = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 18px;
    height: 18px;
    path {
      fill: #808080;
    }
  }
`;

const FuelIconWrapper = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const RefuelValue = styled.span`
  font-size: 14px;
  color: #1890ff;
`;

const ActionsCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-left: auto;
  
  @media (max-width: ${mediaScreenSizes.mobile}) {
    margin-left: 0;
    margin-top: 8px;
    width: 100%;
  }
`;

// New styled components for route display similar to broker modal
const RouteLine = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 20px;
  flex-wrap: wrap;
`;

const RouteAirportCode = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 20px;
  line-height: 22px;
  color: #000000;
  letter-spacing: -0.2px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RouteAirportCodeText = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 20px;
  line-height: 22px;
  color: #000000;
  letter-spacing: -0.2px;
`;

const RouteDateTimeInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 11px;
  line-height: 14px;
  color: #000000;
  opacity: 0.5;
`;

const RouteArrowWithPax = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding-top: 4px;
`;

const RouteArrowWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const RoutePaxLabel = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: #000000;
  opacity: 0.5;
`;

const RequestedPriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RequestedPriceOriginal = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 11px;
  line-height: 14px;
  color: #808080;
  opacity: 0.7;
`;

const StyledAcceptIcon = styled(AcceptIcon)<{ $active?: boolean }>`
  width: 21px;
  height: 21px;
  
  path {
    fill: ${({$active}) => ($active ? "#ffffff" : "#8c8c8c")};
    transition: fill 0.2s ease;
  }
`;

const StatusButton = styled.button<{ $variant: "accept" | "decline"; $active: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: ${({$active, $variant}) =>
    $active ? "none" : $variant === "accept" ? "1px solid #d9d9d9" : "1px solid #d9d9d9"};
  background-color: ${({$active, $variant}) => {
    if (!$active) return "transparent";
    return $variant === "accept" ? "#448df7" : "#ff3b30";
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({disabled}) => (disabled ? "not-allowed" : "pointer")};
  transition: background-color 0.2s ease, border-color 0.2s ease;
  color: ${({$active}) => ($active ? "#ffffff" : "#8c8c8c")};
  opacity: ${({disabled}) => (disabled ? 0.6 : 1)};

  &:hover:not(:disabled) {
    background-color: ${({$variant}) => ($variant === "accept" ? "#3a7fe0" : "#e6332a")};
    border-color: transparent;
    color: #fff;
    
    ${StyledAcceptIcon} {
      path {
        fill: #ffffff;
      }
    }
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const CheckSymbol = styled.span`
  position: relative;
  width: 10px;
  height: 10px;
  display: block;
  transform: rotate(-45deg);
  border-left: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  color: inherit;
`;

const CloseSymbol = styled.span`
  position: relative;
  width: 12px;
  height: 12px;
  display: block;

  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 12px;
    height: 2px;
    background-color: currentColor;
    transform-origin: center;
  }

  &::before {
    transform: translate(-50%, -50%) rotate(45deg);
  }

  &::after {
    transform: translate(-50%, -50%) rotate(-45deg);
  }
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  
  @media (max-width: ${mediaScreenSizes.mobile}) {
    gap: 12px;
  }
`;

const CheckboxRow = styled.div`
  padding: 12px 0 4px;
  border-top: 1px solid #f0f0f0;
  
  @media (max-width: ${mediaScreenSizes.mobile}) {
    padding: 8px 0 4px;
  }
`;

const WelcomeMessageSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
  padding: 12px;
  background-color: #f5f5f5;
  border-radius: 6px;
  border: 1px solid #e8e8e8;
`;

const WelcomeMessageLabel = styled.span`
  font-size: 12px;
  color: #808080;
  font-weight: 500;
`;

const WelcomeMessageBox = styled.div`
  font-size: 14px;
  color: #000;
  line-height: 1.5;
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const TextareaSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.span`
  font-size: 14px;
  color: #000;
`;

const StyledTextArea = styled(Input.TextArea)`
  border-radius: 6px;
  min-height: 96px;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  @media (max-width: ${mediaScreenSizes.mobile}) {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
`;

const AircraftSelectWrapper = styled.div`
  position: relative;
  display: inline-block;
  
  @media (max-width: ${mediaScreenSizes.mobile}) {
    width: 100%;
  }
`;

const StyledAddAircraftButton = styled(CustomButton)`
  cursor: pointer;
  background-color: #ffffff !important;
  border-color: #1890ff !important;
  color: #1890ff !important;
  z-index: 10;
  
  &:hover {
    background-color: #ffffff !important;
    border-color: #40a9ff !important;
    color: #40a9ff !important;
  }
  
  &:focus {
    background-color: #ffffff !important;
    border-color: #1890ff !important;
    color: #1890ff !important;
  }
  
  @media (max-width: ${mediaScreenSizes.mobile}) {
    width: 100%;
  }
`;

const HiddenAircraftSelect = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  opacity: 0;
  z-index: 2;
  
  .ant-select {
    pointer-events: auto;
    width: 100%;
    height: 100%;
  }
  
  .ant-select-selector {
    pointer-events: auto;
    cursor: pointer;
    width: 100%;
    height: 100%;
  }
`;

const FooterActions = styled.div`
  display: flex;
  gap: 12px;
  
  @media (max-width: ${mediaScreenSizes.mobile}) {
    flex-direction: column;
    width: 100%;
    
    button {
      width: 100%;
    }
  }
`;

const DateTimeModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 16px 0;
  
  /* Override styles from DateAndTimeRangePicker and DateAndTimePicker for modal */
  /* Structure: DateAndTimeRangeWrapper > DatePickersRow > DatePickerWrapper */
  /* Override flex: 1 from DatePickerWrapper in DateAndTimeRangePicker */
  > div:first-child > div > div {
    flex: 0 0 auto !important;
    
    /* Inside DateAndTimePicker: DateAndTimeWrapper contains DatePickerWrapper, TimeInputWrapper, TimezoneSelectWrapper */
    /* Override flex: 1 from DatePickerWrapper in DateAndTimePicker (first child of DateAndTimeWrapper) */
    > div:first-child > div:first-child {
      flex: 0 0 auto !important;
      width: auto !important;
    }
    
    /* Override width: 108px from TimezoneSelectWrapper (last child of DateAndTimeWrapper) */
    > div:first-child > div:last-child {
      width: auto !important;
    }
  }
  
  .ant-picker {
    height: 48px !important;
    border-radius: 6px !important;
  }
  
  input {
    height: 48px !important;
    border-radius: 6px !important;
  }
  
  .ant-select-selector {
    height: 48px !important;
    border-radius: 6px !important;
    
    .ant-select-selection-item,
    .ant-select-selection-placeholder {
      line-height: 46px !important;
      display: flex;
      align-items: center;
    }
  }
`;

const DateRangeWithLocalTime = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
`;

const DatePickersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: flex-start;
  width: 100%;
`;

const DatePickerWithLocalTime = styled.div`
  flex: 1;
  min-width: 290px;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: 2px;

  & > div:first-child {
    height: 48px !important;
  }

  @media (max-width: ${mediaScreenSizes.mobile}) {
    height: auto;
    
    & > div:first-child {
        height: 48px !important;

      &  > div:first-child {
        display: flex;
        flex-direction: column !important;

        & > div:first-child {
          height: 48px !important;
          width: calc(100% - 86px) !important;
        }
      }
    }

    .ant-picker {
      height: 48px !important;
    }
  }
`;

const RangeSeparator = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #666;
  font-weight: 500;
  padding: 0 4px;
  min-width: 20px;
  justify-content: center;
  margin-top: 12px; /* Выравниваем с центром пикеров */
`;

const LocalTimeInfoReadOnly = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  font-size: 12px;
  color: #808080;
  margin-top: 4px;
`;

const DateTimeModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
  
  @media (max-width: ${mediaScreenSizes.mobile}) {
    flex-direction: column;
    width: 100%;
    
    button {
      width: 100%;
    }
  }
`;
